"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Code as Code2, FileText, Link2, Activity, ChartBar as BarChart3, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const deliverables = [{
  id: 1,
  title: "Monthly SEO strategy & roadmap",
  description: "I build and adapt a prioritised 30-day action plan tailored directly to your commercial goals and market shifts.",
  highlight: "Direct strategic roadmap",
  iconName: "Compass"
}, {
  id: 2,
  title: "Ongoing technical SEO maintenance",
  description: "I resolve crawl anomalies, schema gaps, indexation issues, and Core Web Vitals bottlenecks before they impact visibility.",
  highlight: "Hands-on audit fixes",
  iconName: "Code2"
}, {
  id: 3,
  title: "Content optimisation & search intent",
  description: "I audit, rewrite, and guide new content creation so your pages match evolving search queries and outrank competitors.",
  highlight: "Intent-focused copy",
  iconName: "FileText"
}, {
  id: 4,
  title: "Authority building & link acquisition",
  description: "I secure contextual, high-relevance editorial links that build domain authority without risky automated schemes.",
  highlight: "White-hat outreach",
  iconName: "Link2"
}, {
  id: 5,
  title: "Continuous rank & index monitoring",
  description: "I track your core keyword movements, competitor shifts, and search engine algorithm updates to protect your rankings.",
  highlight: "Proactive tracking",
  iconName: "Activity"
}, {
  id: 6,
  title: "Monthly reporting & direct video review",
  description: "I deliver honest, jargon-free performance metrics alongside a personalised Loom walkthrough explaining exact progress.",
  highlight: "100% transparent data",
  iconName: "BarChart3"
}];
const directBenefits = [{
  id: 1,
  text: "Structured 30-day sprint cycles with prioritised deliverables and milestone tracking"
}, {
  id: 2,
  text: "Flexible monthly retainers with zero locked-in long contracts"
}, {
  id: 3,
  text: "Custom strategies designed for sustainable commercial growth"
}, {
  id: 4,
  text: "Clear monthly deliverables with transparent implementation logs"
}];
function getIcon(name: string) {
  switch (name) {
    case "Compass":
      return <Compass className="size-6 text-primary" />;
    case "Code2":
      return <Code2 className="size-6 text-primary" />;
    case "FileText":
      return <FileText className="size-6 text-primary" />;
    case "Link2":
      return <Link2 className="size-6 text-primary" />;
    case "Activity":
      return <Activity className="size-6 text-primary" />;
    case "BarChart3":
      return <BarChart3 className="size-6 text-primary" />;
    default:
      return <Compass className="size-6 text-primary" />;
  }
}
export default function WhatsIncluded() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="whatsincluded" data-nav="light" className="relative w-full bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full bg-secondary text-secondary-foreground border border-border">
            Monthly SEO Retainer
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight font-serif text-foreground">
            What is included in my ongoing SEO service
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            Every deliverable is handled directly by me. I provide personal, hands-on execution, practical strategic guidance, and clear communication every step of the way.
          </p>
        </div>

        {/* Deliverables Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {deliverables.map((item, index) => <motion.div key={item.id} variants={itemVariants} data-index={index}>
              <Card className="h-full border border-border bg-card hover:shadow-md transition-[box-shadow,border-color] duration-200 rounded-xl">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <div className="flex size-12 items-center justify-center rounded-lg bg-secondary/70 border border-border/60">
                    {getIcon(item.iconName)}
                  </div>
                  <span className="text-xs font-medium text-muted-foreground font-mono">
                    0{item.id}
                  </span>
                </CardHeader>
                <CardContent className="pt-2 flex flex-col justify-between h-full">
                  <div>
                    <CardTitle className="text-xl font-semibold font-serif text-card-foreground mb-2">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base text-muted-foreground font-sans leading-relaxed mb-4">
                      {item.description}
                    </CardDescription>
                  </div>
                  <div className="pt-3 border-t border-border/50 flex items-center gap-2 text-xs font-medium text-primary">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{item.highlight}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Value Proposition Callout Banner */}
        <div className="mt-16 md:mt-20 p-8 md:p-10 rounded-2xl bg-secondary/40 border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="size-5 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  Senior Consultant Guarantee
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground mb-4">
                Structured monthly retainers with direct consultant delivery
              </h3>
              <p className="text-sm md:text-base text-muted-foreground font-sans leading-relaxed mb-6">
                Each month runs on an agreed 30-day sprint aligned with your commercial priorities. I handle the strategic roadmap, technical fixes, and content optimisation myself, keeping you updated with transparent implementation logs and direct video reviews.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {directBenefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground">
                    <UserCheck className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{benefit.text}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 flex flex-col items-start lg:items-end justify-center gap-4 pt-6 lg:pt-0 lg:border-l lg:border-border lg:pl-10">
              <div className="text-left lg:text-right">
                <div className="text-sm font-medium text-muted-foreground">Ready for sustainable growth?</div>
                <div className="text-lg font-bold text-foreground font-serif">Let&apos;s review your website together.</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    <span>Book a consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <Link to="/CaseStudiesPage">
                    View case studies
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}